import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch as ReactDispatch,
  type SetStateAction,
} from 'react'
import type { AnyAction, Dispatch } from 'redux'
import {
  API,
  crmServiceUrl,
  errorLogger,
  startLoading,
  stopLoading,
  LoadingTypes,
} from '@labrav/common'
import { type Offer } from '../Offers/OfferCard'

const fetchOffersListAPI = (partnerId: string, opportunityId: string) =>
  API.get(`${crmServiceUrl}/partners/${partnerId}/private_offers`, {
    params: {
      crm_opportunity_id: opportunityId,
      sort_by: 'created_at',
      sort_order: 'DESC',
    },
  })

const OFFERS_LOAD_ERROR = 'Unable to load offers. Please try again.'

async function fetchOffersForHub(
  partnerId: string,
  oppId: string,
  setOffers: ReactDispatch<SetStateAction<Offer[]>>,
  setOffersError: ReactDispatch<SetStateAction<string | null>>,
  isCurrent: () => boolean
): Promise<void> {
  setOffersError(null)
  try {
    const res = await fetchOffersListAPI(partnerId, oppId)
    if (!isCurrent()) return
    setOffers(
      Array.isArray(res.data.private_offers) ? res.data.private_offers : []
    )
    setOffersError(null)
  } catch (error) {
    if (!isCurrent()) return
    setOffers([])
    setOffersError(OFFERS_LOAD_ERROR)
    errorLogger()(error as Error)
  }
}

export const useOffersData = ({
  dispatch,
  partnerId,
  opportunityId,
  authLoading,
  canViewMarketplace,
}: {
  dispatch: Dispatch<AnyAction>
  partnerId?: string
  opportunityId: string
  authLoading: boolean
  canViewMarketplace: boolean
}) => {
  const [offers, setOffers] = useState<Offer[]>([])
  const [offersError, setOffersError] = useState<string | null>(null)
  const offersRequestRef = useRef(0)

  const fetchOffers = useCallback(
    async (opportunityIdOverride?: string) => {
      const oppId = opportunityIdOverride ?? opportunityId
      if (!partnerId?.trim() || authLoading || !canViewMarketplace) return
      const requestId = ++offersRequestRef.current
      const isCurrent = () => offersRequestRef.current === requestId
      dispatch(startLoading(LoadingTypes.GET_DEFAULT_OFFERS))
      await fetchOffersForHub(
        partnerId,
        oppId,
        setOffers,
        setOffersError,
        isCurrent
      ).finally(() => dispatch(stopLoading(LoadingTypes.GET_DEFAULT_OFFERS)))
    },
    [partnerId, opportunityId, authLoading, canViewMarketplace, dispatch]
  )

  useEffect(() => {
    if (
      !opportunityId ||
      !partnerId?.trim() ||
      authLoading ||
      !canViewMarketplace
    ) {
      return
    }
    void fetchOffers(opportunityId)
    return () => {
      offersRequestRef.current += 1
    }
  }, [opportunityId, partnerId, authLoading, canViewMarketplace, fetchOffers])

  return {
    offers,
    offersError,
    setOffers,
    fetchOffers,
  }
}
